import React, { useMemo, useState } from 'react';
import { type Client, DAY_KEYS, type Language } from '../types';
import type { Dict } from '../shared/i18n';
import { DAY_SHORT } from '../shared/i18n';
import { canAdd } from '../shared/entitlements';
import type { PlanLimits } from '../shared/config';
import { Card, Empty, LimitNotice } from '../components/shared';

interface ClientsPageProps {
  clients: Client[];
  lang: Language;
  t: Dict;
  limits: PlanLimits;
  onUpdate: (clients: Client[]) => void;
}

const blank = { name: '', city: '', sector: '', phone: '', address: '', days: [] as string[] };

const ClientsPage: React.FC<ClientsPageProps> = ({ clients, lang, t, limits, onUpdate }) => {
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const gate = canAdd(limits, 'clients', clients.length);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.city, c.sector, c.phone].some((f) => f?.toLowerCase().includes(q)));
  }, [clients, query]);

  const toggleDay = (day: string) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(day) ? f.days.filter((d) => d !== day) : [...f.days, day],
    }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const next: Client = {
      id: editing?.id ?? crypto.randomUUID(),
      name: form.name.trim(),
      city: form.city.trim(),
      sector: form.sector.trim(),
      phone: form.phone.trim() || undefined,
      address: form.address.trim() || undefined,
      deliveryDays: form.days,
    };

    onUpdate(editing ? clients.map((c) => (c.id === editing.id ? next : c)) : [...clients, next]);
    setForm(blank); setOpen(false); setEditing(null);
  };

  const startEdit = (c: Client) => {
    setEditing(c);
    setForm({
      name: c.name, city: c.city, sector: c.sector,
      phone: c.phone ?? '', address: c.address ?? '', days: c.deliveryDays,
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-3 qd-enter">
      {open ? (
        <form onSubmit={submit} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <h2 className="font-extrabold text-slate-900">{editing ? t.saveChanges : t.registerClient}</h2>

          <input
            type="text" placeholder={t.shopName} value={form.name} required
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text" placeholder={t.city} value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
            />
            <input
              type="text" placeholder={t.sector} value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              className="bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
            />
          </div>
          <input
            type="tel" placeholder={t.phone} value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />
          <input
            type="text" placeholder={t.address} value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
          />

          <fieldset className="space-y-2">
            <legend className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
              {t.deliveryDays}
            </legend>
            <div className="flex flex-wrap gap-1.5">
              {DAY_KEYS.map((day, i) => {
                const on = form.days.includes(day);
                return (
                  <button
                    key={day} type="button"
                    onClick={() => toggleDay(day)}
                    aria-pressed={on}
                    className={`min-w-[44px] h-11 px-3 rounded-xl text-xs font-extrabold border ${
                      on ? 'bg-[#C1272D] text-white border-[#C1272D]' : 'bg-white text-slate-500 border-slate-200'
                    }`}
                  >
                    {DAY_SHORT[lang][i]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); setEditing(null); setForm(blank); }}
              className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-xl font-extrabold"
            >{t.cancel}</button>
            <button type="submit" className="flex-1 bg-[#C1272D] text-white py-3 rounded-xl font-extrabold">
              {t.saveClient}
            </button>
          </div>
        </form>
      ) : gate.allowed ? (
        <button
          onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
          className="w-full bg-white border-2 border-dashed border-[#006233] text-[#006233] font-extrabold p-4 rounded-2xl min-h-[56px]"
        >
          👤 {t.registerClient}
        </button>
      ) : (
        <LimitNotice what="clients" t={t} onUpgrade={() => { /* handled by Settings */ }} />
      )}

      {clients.length > 4 && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          aria-label={t.search}
          className="w-full bg-white border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
        />
      )}

      {filtered.length === 0 ? (
        <Empty icon="👥" title={query ? t.empty : t.noOrders} hint={query ? undefined : t.registerClient} />
      ) : (
        filtered.map((client) => (
          <Card key={client.id} className="flex justify-between items-start gap-3">
            <div className="min-w-0 space-y-1">
              <p className="font-extrabold text-slate-900 truncate">{client.name}</p>
              <p className="text-xs text-slate-500">
                {[client.city, client.sector].filter(Boolean).join(' · ') || '—'}
              </p>
              <div className="flex flex-wrap gap-1 pt-0.5">
                {client.deliveryDays.length === 0
                  ? <span className="text-[10px] text-slate-300">—</span>
                  : DAY_KEYS.filter((d) => client.deliveryDays.includes(d)).map((d) => (
                    <span key={d} className="text-[9px] bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded border border-slate-100 font-bold">
                      {DAY_SHORT[lang][DAY_KEYS.indexOf(d)]}
                    </span>
                  ))}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              {client.phone && (
                <a
                  href={`tel:${client.phone}`}
                  aria-label={`${t.call} ${client.name}`}
                  className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center"
                >📞</a>
              )}
              <button
                onClick={() => startEdit(client)}
                aria-label={`${t.edit} ${client.name}`}
                className="w-10 h-10 rounded-xl bg-slate-50"
              >✏️</button>
              <button
                onClick={() => {
                  if (confirm(`${client.name}\n${t.confirmDelete}`)) {
                    onUpdate(clients.filter((c) => c.id !== client.id));
                  }
                }}
                aria-label={`${t.delete} ${client.name}`}
                className="w-10 h-10 rounded-xl bg-slate-50"
              >🗑️</button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default ClientsPage;
